/*
 * Qikify FAQs: one question open at a time, with a smooth open/close.
 * Used by sections/qikify-faqs.liquid, only when the section setting "Open one at a time,
 * smooth animation" is on: the section then renders `data-qfaq-single` on its wrapper and
 * loads this file. With the setting off nothing here runs. Ported from the page-specific
 * assets/section-qikify-ap-faq.js (partner program page). Works on the native
 * <details> elements the section already renders, so there is no extra markup.
 * "One at a time" means one per section (all columns together), not one per column.
 * Event delegation, so a section re-rendered by the Theme Editor needs no re-init.
 *
 * How the animation works
 *  - A click on a <summary> is intercepted (preventDefault) and we drive the <details> ourselves
 *    with the Web Animations API: the box height animates between "question only" and "full",
 *    and the answer fades. Nothing is written to inline styles, so a cancelled animation can
 *    never leave an item stuck half open.
 *  - Opening: the `open` attribute is set at once and the height grows from the current height.
 *    Closing: `open` stays until the animation ends (otherwise the browser hides the answer
 *    immediately), then it is removed. The previously open item closes in the same frame with
 *    the same timing, so the list does not jump.
 *  - Every animation starts from the *current* height, so clicking again mid-animation reverses
 *    smoothly instead of snapping.
 *  - `data-qfaq-open` on each <details> is the intended state (the CSS uses it for the chevron
 *    and colours while an item is still closing). `open` is the real state.
 *  - Reduced motion, no Web Animations support, keyboard Escape (theme's global.js removes
 *    `open`) and Theme Editor block-select take the plain native path: instant, still
 *    single-open.
 */
(function () {
  if (window.__qikifyFaqsInit) return;
  window.__qikifyFaqsInit = true;

  var ROOT = '[data-qfaq-single]';
  var HEIGHT_MS = 300;
  var FADE_IN_MS = 300;
  var FADE_OUT_MS = 200;
  var EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

  var reduceQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function canAnimate() {
    if (typeof Element.prototype.animate !== 'function') return false;
    return !(reduceQuery && reduceQuery.matches);
  }

  function isOpen(d) {
    // Intended state; falls back to the real attribute before init has run.
    return d.dataset.qfaqOpen ? d.dataset.qfaqOpen === 'true' : d.hasAttribute('open');
  }

  function setIntent(d, open) {
    d.dataset.qfaqOpen = open ? 'true' : 'false';
    var summary = d.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  // Real `open` attribute as last written by this script. A toggle event that disagrees with
  // it means somebody else (Escape handler, Theme Editor, native click) changed it.
  function setAttr(d, open) {
    if (open) d.setAttribute('open', '');
    else d.removeAttribute('open');
    d.__qfaqAttr = open;
  }

  function cancelAnims(d) {
    var list = d.__qfaqAnims;
    d.__qfaqAnims = null;
    if (!list) return;
    list.forEach(function (a) {
      a.onfinish = null;
      a.oncancel = null;
      try { a.cancel(); } catch (e) { /* already gone */ }
    });
  }

  function borderHeight(d) {
    var cs = getComputedStyle(d);
    return (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
  }

  // Animate one <details> to open or closed, starting from wherever it is right now.
  function go(d, wantOpen) {
    var summary = d.querySelector('summary');
    var content = d.querySelector('.accordion__content');
    if (!summary) return;

    var wasOpenAttr = d.hasAttribute('open');
    var startH = d.getBoundingClientRect().height;
    var startO = 0;
    if (wasOpenAttr && content) {
      startO = parseFloat(getComputedStyle(content).opacity);
      if (isNaN(startO)) startO = 1;
    }

    cancelAnims(d);
    setIntent(d, wantOpen);

    var endH;
    if (wantOpen) {
      setAttr(d, true);
      endH = d.getBoundingClientRect().height; // natural, full height
    } else {
      endH = summary.getBoundingClientRect().height + borderHeight(d);
    }

    function settle() {
      d.__qfaqAnims = null;
      if (!wantOpen) setAttr(d, false);
    }

    // Nothing to animate (hidden section, zero height, already there).
    if (!isFinite(startH) || !isFinite(endH) || Math.abs(startH - endH) < 1) {
      cancelAnims(d);
      settle();
      return;
    }

    var fill = wantOpen ? 'none' : 'forwards';
    var heightAnim = d.animate(
      [
        { height: startH + 'px', overflow: 'hidden' },
        { height: endH + 'px', overflow: 'hidden' }
      ],
      { duration: HEIGHT_MS, easing: EASE, fill: fill }
    );
    var anims = [heightAnim];
    if (content) {
      anims.push(
        content.animate(
          [{ opacity: startO }, { opacity: wantOpen ? 1 : 0 }],
          { duration: wantOpen ? FADE_IN_MS : FADE_OUT_MS, easing: wantOpen ? 'ease-out' : 'ease-in', fill: fill }
        )
      );
    }
    d.__qfaqAnims = anims;

    // If something else cancels the animation (devtools, document.getAnimations().forEach(...)),
    // settle to the intended state instead of leaving a half-closed item: closing finishes
    // (removes `open`), opening just stays open. Our own cancels clear this handler first.
    var onExternalCancel = function () {
      if (d.__qfaqAnims !== anims) return;
      if (!wantOpen) setAttr(d, false);
      cancelAnims(d);
    };
    anims.forEach(function (a) { a.oncancel = onExternalCancel; });

    heightAnim.onfinish = function () {
      // Close: remove `open` first, then drop the forwards-filled animations, in the same task,
      // so no frame is ever painted with the answer showing at full height.
      if (!wantOpen) setAttr(d, false);
      cancelAnims(d);
    };
  }

  // Instant close, used when another item is opened by a non-animated path.
  function closeNow(d) {
    cancelAnims(d);
    setIntent(d, false);
    setAttr(d, false);
  }

  function closeOthersNow(opened) {
    var root = opened.closest(ROOT);
    if (!root) return;
    root.querySelectorAll('details').forEach(function (other) {
      if (other !== opened && (other.hasAttribute('open') || isOpen(other))) closeNow(other);
    });
  }

  // Click on a question: animate it, and close the open one at the same time.
  // Bubble phase on purpose: the theme's own summary click handler (global.js) has already run
  // by now, and our aria-expanded value is the one that stays.
  document.addEventListener('click', function (event) {
    var summary = event.target && event.target.closest ? event.target.closest('summary') : null;
    if (!summary) return;
    var d = summary.parentNode;
    if (!d || d.tagName !== 'DETAILS') return;
    var root = d.closest(ROOT);
    if (!root || !canAnimate()) return; // native path, handled by the toggle listener below

    event.preventDefault();
    var wantOpen = !isOpen(d);
    if (wantOpen) {
      root.querySelectorAll('details').forEach(function (other) {
        if (other !== d && (other.hasAttribute('open') || isOpen(other))) go(other, false);
      });
    }
    go(d, wantOpen);
  });

  // "toggle" does not bubble, so listen in the capture phase on document.
  // It fires for our own attribute changes too; those match __qfaqAttr and are ignored.
  document.addEventListener(
    'toggle',
    function (event) {
      var d = event.target;
      if (!d || d.tagName !== 'DETAILS' || !d.closest(ROOT)) return;
      if (d.__qfaqAttr === d.open) return; // our own change
      // Somebody else changed it (native click, Escape, Theme Editor): follow it, instantly.
      cancelAnims(d);
      d.__qfaqAttr = d.open;
      setIntent(d, d.open);
      if (d.open) closeOthersNow(d);
    },
    true
  );

  function init(scope) {
    (scope || document).querySelectorAll(ROOT).forEach(function (root) {
      // If more than one is open on load (e.g. edited by hand), keep the first.
      var seen = false;
      root.querySelectorAll('details').forEach(function (d) {
        if (d.hasAttribute('open')) {
          if (seen) d.removeAttribute('open');
          seen = true;
        }
        cancelAnims(d);
        d.__qfaqAttr = d.hasAttribute('open');
        setIntent(d, d.__qfaqAttr);
      });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  // Theme Editor: re-sync after a section reloads, and open the block that was selected.
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:block:select', function (event) {
    var details = event.target.querySelector && event.target.querySelector('details');
    if (!details || !details.closest(ROOT)) return;
    if (details.hasAttribute('open') && !isOpen(details)) {
      // Mid-close: `open` is still there, so setting details.open = true would do nothing and
      // the item would finish closing. Reverse the closing animation instead.
      closeOthersNow(details);
      go(details, true);
    } else {
      details.open = true; // instant, via the toggle listener
    }
  });
})();
